import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import {
  Award,
  Download,
  Share2,
  Calendar,
  BookOpen,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { certificatesAPI } from '../lib/api';
import { toast } from 'sonner';

export default function CertificatesPage() {
  const [loading, setLoading] = useState(true);
  const [certificates, setCertificates] = useState([]);

  useEffect(() => {
    fetchCertificates();
  }, []);

  const fetchCertificates = async () => {
    try {
      const response = await certificatesAPI.getMy();
      setCertificates(response.data);
    } catch (error) {
      toast.error('Failed to load certificates');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (cert) => {
    // Create a simple certificate HTML and open in new window for printing
    const certHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Certificate - ${cert.course_title}</title>
        <style>
          body { font-family: 'Georgia', serif; margin: 0; padding: 40px; }
          .certificate { 
            border: 8px double #1e1e1e; 
            padding: 60px; 
            text-align: center;
            max-width: 800px;
            margin: 0 auto;
            background: linear-gradient(to bottom, #fdfbf7, #fff);
          }
          .title { font-size: 48px; font-weight: bold; color: #2563eb; margin-bottom: 20px; }
          .subtitle { font-size: 24px; color: #666; margin-bottom: 40px; }
          .name { font-size: 36px; font-weight: bold; color: #1e1e1e; margin: 30px 0; }
          .course { font-size: 24px; color: #1e1e1e; margin: 20px 0; }
          .date { font-size: 16px; color: #666; margin-top: 40px; }
          .cert-number { font-size: 12px; color: #999; margin-top: 20px; }
          .seal { width: 100px; height: 100px; margin: 30px auto; }
        </style>
      </head>
      <body>
        <div class="certificate">
          <div class="title">Certificate of Completion</div>
          <div class="subtitle">This is to certify that</div>
          <div class="name">${cert.user_name}</div>
          <div class="subtitle">has successfully completed</div>
          <div class="course">"${cert.course_title}"</div>
          <div class="date">Issued on ${new Date(cert.issued_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
          <div class="cert-number">Certificate Number: ${cert.certificate_number}</div>
        </div>
        <script>window.print();</script>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(certHtml);
    printWindow.document.close();
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="certificates-page">
        {/* Header */}
        <div>
          <h1 className="font-sketch text-4xl text-ink">My Certificates</h1>
          <p className="text-muted-foreground mt-1">
            View and download your earned certificates
          </p>
        </div>

        {/* Certificates Grid */}
        {certificates.length > 0 ? (
          <div className="grid md:grid-cols-2 gap-6">
            {certificates.map((cert) => (
              <div
                key={cert.id}
                className="card-sketchy relative overflow-hidden"
                data-testid={`certificate-${cert.id}`}
              >
                {/* Decorative corner */}
                <div className="absolute top-0 right-0 w-20 h-20 bg-yellow-300 transform rotate-45 translate-x-10 -translate-y-10" />
                
                <div className="relative z-10">
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 bg-yellow-100 rounded-lg flex items-center justify-center border-2 border-black">
                      <Award className="w-8 h-8 text-yellow-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-lg text-ink">{cert.course_title}</h3>
                      <p className="text-sm text-muted-foreground">{cert.user_name}</p>
                    </div>
                  </div>

                  <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                      <Calendar className="w-4 h-4" />
                      <span>
                        Issued: {new Date(cert.issued_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground font-mono">
                      {cert.certificate_number}
                    </p>
                  </div>

                  <div className="mt-4 flex gap-3">
                    <Button
                      onClick={() => handleDownload(cert)}
                      className="btn-sketchy flex-1"
                      data-testid={`download-cert-${cert.id}`}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download
                    </Button>
                    <Button
                      variant="outline"
                      className="border-2 border-black"
                      onClick={() => {
                        navigator.clipboard.writeText(
                          `I earned a certificate in "${cert.course_title}" from SketchLearn! 🎉`
                        );
                        toast.success('Copied to clipboard!');
                      }}
                    >
                      <Share2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="card-sketchy text-center py-16">
            <Award className="w-24 h-24 text-muted-foreground mx-auto mb-6" />
            <h3 className="font-sketch text-2xl text-ink mb-2">No certificates yet</h3>
            <p className="text-muted-foreground mb-6">
              Complete courses to earn certificates
            </p>
            <a href="/courses">
              <Button className="btn-sketchy">
                <BookOpen className="w-4 h-4 mr-2" />
                Browse Courses
              </Button>
            </a>
          </div>
        )}
      </div>
    </Layout>
  );
}
